import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, devWarn } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out
  const [error, setError] = useState("");
  const [lock, setLock] = useState(null); // { retryAfter, remaining }

  // Session revoked (password/permissions changed) or expired -> back to login.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        const url = err.config?.url || "";
        if (err.response?.status === 401 && !url.startsWith("/auth/")) {
          setError("Sesiunea a expirat. Autentifica-te din nou.");
          setUser(false);
        }
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    let mounted = true;
    api
      .get("/auth/me")
      .then((r) => {
        if (mounted) setUser(r.data);
      })
      .catch(() => {
        if (mounted) setUser(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const login = async (email, password) => {
    setError("");
    setLock(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Fetch full /auth/me to capture agent_name and any other extended fields.
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch {
        setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      }
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(formatApiErrorDetail(detail) || e.message);
      if (detail && typeof detail === "object" && !Array.isArray(detail)) {
        setLock({ retryAfter: detail.retry_after || 0, remaining: detail.remaining });
      }
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      devWarn("logout request failed (clearing session anyway):", err);
    }
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      return null;
    }
  };

  // Memoize context value to prevent unnecessary re-renders of consumers
  // when AuthProvider itself re-renders for unrelated reasons.
  const value = useMemo(
    () => ({ user, login, logout, error, setError, lock, setLock, refreshUser }),
    [user, error, lock]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
